// ============================================================
// LoadoutMenu - save the acting character's weapons as a named kit,
// or stamp a saved kit onto them.
//
// Stamping is a COPY, not a link: the weapons land on the combatant's
// own sheet with fresh ids, so two people carrying "the same" bolter
// still track their own ammo. Editing either afterwards leaves the
// other alone.
//
// A SHELF (plan armoury, part 6), grouped as the bench's are: My library
// (the hosting GM's Assets; managed on the Assets page, so never deleted
// here), then This table (a session's shared kits) or This browser (play
// with no session). Saving says where it goes, one button per place.
// ============================================================
import { useState } from 'react';
import clsx from '../utils/clsx';
import type { Combatant, Loadout } from '../engine/roster/types';
import common from '../styles/common.module.css';
import styles from '../styles/CombatantPicker.module.css';
import shelf from '../styles/Submenus.module.css';

export interface LoadoutMenuProps {
  actor: Combatant | null;
  /** The session's kits, or this browser's with no session (`place` says which). */
  loadouts: Loadout[];
  place: 'table' | 'browser';
  /** The GM's library kits; absent without a library. */
  libraryKits?: Loadout[] | null;
  onSave: (name: string) => void;
  onSaveToLibrary?: ((name: string) => void) | null;
  onApply: (loadout: Loadout) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

const PLACE_WORDS = {
  table: { group: 'This table', save: "on this table's shelf" },
  browser: { group: 'This browser', save: 'in this browser' },
} as const;

export default function LoadoutMenu(props: LoadoutMenuProps) {
  const { actor, loadouts, place, libraryKits, onSave, onSaveToLibrary, onApply, onDelete, disabled = false } = props;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const weaponCount = actor?.sheet.weapons?.length ?? 0;

  /** Save under the typed name, wherever this button says. */
  const saveTo = (save: (name: string) => void) => () => {
    if (!name.trim() || !actor) return;
    save(name.trim());
    setName('');
    setOpen(false);
  };
  const apply = (l: Loadout) => {
    onApply(l);
    setOpen(false);
  };
  const destinations = [
    ...(onSaveToLibrary ? [{ label: 'to my library', save: saveTo(onSaveToLibrary) }] : []),
    { label: PLACE_WORDS[place].save, save: saveTo(onSave) },
  ];
  const nothing = loadouts.length === 0 && !libraryKits?.length;

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={clsx(common.btnSmall, styles.trigger)}
        aria-label="Weapon loadouts"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        Loadouts <span aria-hidden="true">&#9660;</span>
      </button>

      {open && (
        <div className={clsx(common.dropdown, styles.menu)} role="menu" aria-label="Weapon loadouts">
          {nothing && <div className={styles.empty}>No saved loadouts yet.</div>}
          {libraryKits && (
            // The library's kits are managed on the Assets page: armed from here, never deleted here.
            <KitGroup
              title="My library"
              testId="kit-shelf-mine"
              kits={libraryKits}
              canApply={!!actor}
              onApply={apply}
            />
          )}
          <KitGroup
            title={PLACE_WORDS[place].group}
            testId={`kit-shelf-${place}`}
            kits={loadouts}
            canApply={!!actor}
            onApply={apply}
            onDelete={(l) => onDelete(l.id)}
          />
          <div className={styles.footer}>
            {actor ? (
              <SaveRow weaponCount={weaponCount} name={name} onName={setName} destinations={destinations} />
            ) : (
              <div className={styles.empty}>Act as someone to save their kit.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** One place's kits, under its heading with its count. */
function KitGroup(p: {
  title: string;
  testId: string;
  kits: Loadout[];
  canApply: boolean;
  onApply: (l: Loadout) => void;
  onDelete?: (l: Loadout) => void;
}) {
  return (
    <section className={shelf.shelfGroup} data-testid={p.testId}>
      <h4 className={shelf.shelfHead}>
        {p.title} <span className={shelf.shelfCount}>{p.kits.length}</span>
      </h4>
      {p.kits.map((l) => (
        <LoadoutItem
          key={l.id}
          loadout={l}
          canApply={p.canApply}
          onApply={() => p.onApply(l)}
          onDelete={p.onDelete ? () => p.onDelete!(l) : undefined}
        />
      ))}
    </section>
  );
}

interface ItemProps {
  loadout: Loadout;
  canApply: boolean;
  onApply: () => void;
  /** Absent for a library kit: the Assets page manages those. */
  onDelete?: () => void;
}

/** A saved kit: its name arms the actor with a copy; ✕ deletes it. */
function LoadoutItem({ loadout: l, canApply, onApply, onDelete }: ItemProps) {
  return (
    <div className={clsx(common.dropdownItem, styles.item)} role="menuitem">
      <button
        type="button"
        className={styles.applyBtn}
        aria-label={`Arm with ${l.name}`}
        disabled={!canApply}
        onClick={onApply}
      >
        {l.name}
        <span className={styles.more}> ({l.weapons.length})</span>
      </button>
      {onDelete && (
        <button
          type="button"
          className={styles.remove}
          aria-label={`Delete loadout ${l.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          {'✕'}
        </button>
      )}
    </div>
  );
}

interface SaveRowProps {
  weaponCount: number;
  name: string;
  onName: (name: string) => void;
  /** Where the kit may go, one button each: "to my library", "on this table's shelf", "in this browser". */
  destinations: { label: string; save: () => void }[];
}

/** Name the actor's current weapons as a kit. The typed name outlives the menu closing. */
function SaveRow({ weaponCount, name, onName, destinations }: SaveRowProps) {
  // Enter saves where the table (or this browser) keeps kits: the place a kit always went.
  const here = destinations[destinations.length - 1];
  return (
    <>
      <input
        className={common.input}
        placeholder={`Save ${weaponCount} weapon${weaponCount === 1 ? '' : 's'} as…`}
        aria-label="New loadout name"
        value={name}
        onChange={(e) => onName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && here.save()}
      />
      {destinations.map((d) => (
        <button key={d.label} type="button" className={common.btnSmall} onClick={d.save} disabled={!name.trim()}>
          Save loadout {d.label}
        </button>
      ))}
    </>
  );
}
